import React, { useState, useRef } from "react";
import axios from "axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ChevronLeft, ArrowRight, MapPin, Clock, Tag, Compass, Check,
  Loader2, Sun, Mountain, Users, Images,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useFormatPrice } from "@/contexts/CurrencyContext";
import { HOTELS } from "@/lib/data";
import { getHotelDetails } from "@/lib/hotelDetails";
import { resolveActivity } from "@/lib/activitySlug";
import BrandMonogramEdge from "@/components/BrandMonogramEdge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

// Build a 3-bullet highlight list from the description text — uses
// the first 3 short fragments, then falls back to generic value props.
const buildHighlights = (descText) => {
  if (!descText) return null;
  const fragments = descText
    .split(/[.;—–]\s+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 14 && s.length < 140);
  return fragments.slice(0, 3);
};

const ActivityDetailPage = ({ type }) => {
  // `activity` is the new URL slot — a slug (e.g. `atv-dune-ride`) or an
  // integer index (legacy). `idx` is kept for backwards compatibility with
  // the original `/hotels/<slug>/excursions/:idx` route.
  const { slug, activity, idx } = useParams();
  const { t, lang } = useLanguage();
  const { parse: parsePrice } = useFormatPrice();
  const navigate = useNavigate();
  const formRef = useRef(null);

  const hotel = HOTELS.find((h) => h.slug === slug);
  // Fix: `getHotelDetails` expects the hotel SLUG string, not the hotel
  // object. Passing the object made every activity page fall through to
  // the default excursion list — so slug-based URLs like
  // `/excursions/atv-dune-ride` (which only exists in Kasbah's overrides)
  // silently 404'd. Passing the slug restores each hotel's own list.
  const details = hotel ? getHotelDetails(hotel.slug) : null;
  const list = details ? (type === "nearby" ? details.destination : details.excursions) : [];
  const resolved = resolveActivity(list, activity || idx);
  const item = resolved ? resolved.item : null;
  const index = resolved ? resolved.index : NaN;

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    preferred_date: "",
    guests: 2,
    notes: "",
  });
  const [status, setStatus] = useState({ submitting: false, success: false, error: false });

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    const firstInput = formRef.current?.querySelector("input[name='full_name']");
    if (firstInput) setTimeout(() => firstInput.focus(), 600);
  };

  if (!hotel || !item) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="activity-not-found">
        <p className="font-serif-x text-3xl text-[#2C2621] mb-6">
          {pick({
            en: "We couldn't find this experience.",
            fr: "Cette expérience est introuvable.",
            es: "No encontramos esta experiencia.",
          }, lang)}
        </p>
        <Link to={hotel ? `/hotels/${hotel.slug}` : "/hotels"} className="text-[#C16542] underline">
          ← {t("common.back")}
        </Link>
      </div>
    );
  }

  const itemTitle = pick(item.title, lang);
  const itemDesc = pick(item.desc, lang);
  // Optional rich long-form content — an ARRAY of paragraph strings per
  // locale, e.g. `body: { en: ["…", "…"], es: [...] }`. When present it
  // is rendered UNDER the summary desc as several paragraphs so editors
  // can craft real landing copy without touching the component tree.
  // Falls back to null when the item only carries the short `desc`.
  const itemBodyRaw = item.body ? pick(item.body, lang) : null;
  const itemBodyParagraphs = Array.isArray(itemBodyRaw)
    ? itemBodyRaw.filter((s) => typeof s === "string" && s.trim().length > 0)
    : (typeof itemBodyRaw === "string" && itemBodyRaw.trim().length > 0
      ? itemBodyRaw.split(/\n{2,}/).map((s) => s.trim()).filter(Boolean)
      : []);
  const heroImage = (hotel.gallery && hotel.gallery[(index + 1) % (hotel.gallery.length || 1)]) || hotel.image;
  const galleryPool = Array.from(new Set([hotel.image, ...(hotel.gallery || [])])).filter(Boolean);

  // Practical info varies by type
  const isExcursion = type === "excursions";
  const practical = isExcursion
    ? {
        duration: pick(item.duration, lang) || (typeof item.duration === "string" ? item.duration : "—"),
        price: pick(item.price, lang) || (typeof item.price === "string" ? item.price : pick({ en: "On request", fr: "Sur demande", es: "Bajo petición" }, lang)),
        difficulty: pick({ en: "Easy to moderate", fr: "Facile à modéré", es: "Fácil a moderado" }, lang),
        group: pick({ en: "Private or shared", fr: "Privé ou partagé", es: "Privado o compartido" }, lang),
        season: pick({ en: "Year-round", fr: "Toute l'année", es: "Todo el año" }, lang),
      }
    : {
        distance: pick(item.distance, lang) || (typeof item.distance === "string" ? item.distance : "—"),
        access: pick({ en: "Private car or guided transfer", fr: "Voiture privée ou transfert guidé", es: "Coche privado o traslado guiado" }, lang),
        duration: pick({ en: "Half-day visit", fr: "Visite d'une demi-journée", es: "Visita de medio día" }, lang),
        best_time: pick({ en: "Morning or late afternoon", fr: "Matin ou fin d'après-midi", es: "Mañana o última hora de la tarde" }, lang),
      };

  const highlights = buildHighlights(itemDesc) || [
    pick({ en: "Designed with our local guides", fr: "Conçu avec nos guides locaux", es: "Diseñado con nuestros guías locales" }, lang),
    pick({ en: "Private or in a small group", fr: "En privé ou en petit groupe", es: "Privado o en grupo reducido" }, lang),
    pick({ en: "Booked & coordinated by the concierge", fr: "Réservé et coordonné par la conciergerie", es: "Reservado y coordinado por el conserje" }, lang),
  ];

  const overlineLabel = isExcursion
    ? pick({ en: "Experience & excursion", fr: "Expérience & excursion", es: "Experiencia y excursión" }, lang)
    : pick({ en: "Discover nearby", fr: "À découvrir tout près", es: "Descubrir cerca" }, lang);

  const requestLabel = pick({ en: "Request information", fr: "Demander des informations", es: "Solicitar información" }, lang);
  const addToBookingLabel = pick({
    en: "Add to a booking request",
    fr: "Ajouter à une demande de séjour",
    es: "Añadir a una solicitud de estancia",
  }, lang);

  const submitForm = async (e) => {
    e.preventDefault();
    if (status.submitting) return;
    setStatus({ submitting: true, success: false, error: false });
    try {
      await axios.post(`${API}/experience-requests`, {
        full_name: form.full_name,
        email: form.email,
        phone: form.phone || null,
        experience_key: `${hotel.slug}:${type}:${index}`,
        experience_title: `${itemTitle} — ${hotel.name}`,
        preferred_date: form.preferred_date || null,
        guests: Number(form.guests) || 2,
        notes: form.notes || null,
        language: lang,
        source: `activity:${type}`,
      });
      setStatus({ submitting: false, success: true, error: false });
      setForm({ full_name: "", email: "", phone: "", preferred_date: "", guests: 2, notes: "" });
    } catch {
      setStatus({ submitting: false, success: false, error: true });
    }
  };

  return (
    <div data-testid={`page-activity-${type}-${slug}-${index}`} className="bg-[#FDFBF7] text-[#2C2621]">
      {/* HERO */}
      <section className="relative min-h-[72vh] flex flex-col justify-end" data-testid="activity-hero" data-cms-scope={`activity.${hotel.slug}.${type}.${index}`}>
        <img src={heroImage} alt={itemTitle} data-cms-role="hero" className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async"/>
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/92 via-[#1A1513]/45 to-[#1A1513]/15" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 flex flex-col pb-16 text-[#FDFBF7]">
          <button
            onClick={() => navigate(-1)}
            className="mb-6 inline-flex items-center gap-1 text-xs tracking-[0.25em] uppercase text-[#FDFBF7]/80 hover:text-[#D4A373] w-fit"
            data-testid="activity-back-button"
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
            {t("common.back")}
          </button>

          <p className="overline !text-[#D4A373]">{overlineLabel}</p>
          <h1 className="font-serif-x mt-3 text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[1.02] max-w-5xl">
            {itemTitle}
          </h1>

          <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-[#FDFBF7]/80">
            <Link
              to={`/hotels/${hotel.slug}`}
              className="inline-flex items-center gap-1.5 text-[#D4A373] hover:text-[#FDFBF7] transition-colors"
              data-testid="activity-hotel-link"
            >
              <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />
              {hotel.name} · {hotel.location}
            </Link>
            {isExcursion ? (
              <span className="inline-flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
                {practical.duration}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5">
                <Compass className="w-3.5 h-3.5" strokeWidth={1.5} />
                {practical.distance}
              </span>
            )}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={scrollToForm}
              className="bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-8 py-4 text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2"
              data-testid="activity-cta-request"
            >
              {requestLabel}
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <Link
              to="/booking"
              state={{ prefillItem: { hotel: hotel.name, activity: itemTitle } }}
              className="border border-[#FDFBF7]/40 text-[#FDFBF7] hover:border-[#FDFBF7] px-6 py-4 text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2"
              data-testid="activity-cta-add-booking"
            >
              {addToBookingLabel}
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      <BrandMonogramEdge size="xl" opacity={0.6} testIdPrefix="page-hero-brand" />
      </section>

      {/* STORY + GALLERY */}
      <section className="py-20 md:py-28" data-testid="activity-story">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <p className="overline">{overlineLabel}</p>
            <p className="font-serif-x mt-4 text-3xl md:text-4xl leading-[1.25]">
              {itemTitle}
            </p>
            <p className="mt-8 text-lg text-[#5C5248] leading-relaxed">
              {itemDesc}
            </p>
            {itemBodyParagraphs.length > 0 ? (
              <div
                className="mt-6 space-y-4 text-base text-[#5C5248] leading-relaxed"
                data-testid="activity-body"
              >
                {itemBodyParagraphs.map((paragraph, i) => (
                  <p key={`body-p-${i}`}>{paragraph}</p>
                ))}
              </div>
            ) : null}
            <p className="mt-5 text-base text-[#5C5248] leading-relaxed">
              {pick({
                en: `An experience curated from ${hotel.name}. Our concierge will tailor every detail — pace, timing, dietary needs, language of your guide — to the rhythm you'd like for the day.`,
                fr: `Une expérience composée depuis ${hotel.name}. Notre conciergerie ajuste chaque détail — rythme, horaires, régime alimentaire, langue du guide — au rythme que vous souhaitez.`,
                es: `Una experiencia compuesta desde ${hotel.name}. Nuestro conserje ajusta cada detalle — ritmo, horarios, régimen alimenticio, idioma del guía — al ritmo que desee.`,
              }, lang)}
            </p>
          </div>
          <div className="md:col-span-5">
            {/* Scoped exception: Kasbah Arfoud · Excursions · index 0 shows
                5 images (1 panoramic + 4 squares). All other Experience
                & Excursion galleries keep the original 1 + 3 layout. */}
            {(() => {
              const isKasbahArfoudExcursion0 =
                hotel.slug === "kasbah-hotel-xaluca-arfoud" &&
                type === "excursions" &&
                index === 0;
              const galleryLimit = isKasbahArfoudExcursion0 ? 5 : 4;
              return (
                <div className="grid grid-cols-2 gap-3" data-testid="activity-gallery" data-cms-scope={`activity.${hotel.slug}.${type}.${index}.gallery`}>
                  {galleryPool.slice(0, galleryLimit).map((g, i) => (
                    <div
                      key={`g-${i}`}
                      className={`relative overflow-hidden ${i === 0 ? "col-span-2 aspect-[16/10]" : "aspect-square"}`}
                      data-testid={`activity-gallery-${i}`}
                    >
                      <img src={g} alt={`${itemTitle} ${i + 1}`} data-cms-role={`tile.${i}`} className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async"/>
                    </div>
                  ))}
                  {galleryPool.length === 0 && (
                    <div className="col-span-2 aspect-[16/10] bg-[#F2EBE1] flex items-center justify-center text-[#5C5248]/60">
                      <Images className="w-8 h-8" strokeWidth={1.3} />
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS + PRACTICAL */}
      <section className="bg-[#F2EBE1]/50 py-20 md:py-28 border-y border-[#2C2621]/10" data-testid="activity-info">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <p className="overline">
              {pick({ en: "Highlights", fr: "Temps forts", es: "Aspectos destacados" }, lang)}
            </p>
            <h2 className="font-serif-x mt-4 text-4xl md:text-5xl tracking-tight leading-[1.05]">
              {pick({ en: "What makes it memorable", fr: "Ce qui le rend mémorable", es: "Lo que lo hace memorable" }, lang)}
            </h2>
            <ul className="mt-10 space-y-4" data-testid="activity-highlights">
              {highlights.map((h, i) => (
                <li key={`h-${i}`} className="flex items-start gap-4">
                  <span className="mt-1.5 w-6 h-6 rounded-full bg-[#C16542] text-[#FDFBF7] flex items-center justify-center shrink-0">
                    <Check className="w-3.5 h-3.5" strokeWidth={2.2} />
                  </span>
                  <span className="text-lg text-[#2C2621] leading-snug">{h}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-5">
            <div className="bg-[#FDFBF7] p-8 md:p-10 border border-[#E8DFCF]" data-testid="activity-practical">
              <p className="overline">
                {pick({ en: "Practical", fr: "Pratique", es: "Práctico" }, lang)}
              </p>
              <h3 className="font-serif-x mt-3 text-2xl md:text-3xl">
                {pick({ en: "Plan your day", fr: "Préparez votre journée", es: "Planifique su día" }, lang)}
              </h3>

              <ul className="mt-6 space-y-4 text-sm text-[#2C2621]">
                <li className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-[#C16542] mt-0.5" strokeWidth={1.5} />
                  <div>
                    <div className="overline !text-[#5C5248]">
                      {pick({ en: "Starts from", fr: "Départ depuis", es: "Salida desde" }, lang)}
                    </div>
                    <div className="mt-1">{hotel.name} · {hotel.location}</div>
                  </div>
                </li>

                {isExcursion ? (
                  <>
                    <li className="flex items-start gap-3">
                      <Clock className="w-4 h-4 text-[#C16542] mt-0.5" strokeWidth={1.5} />
                      <div>
                        <div className="overline !text-[#5C5248]">{pick({ en: "Duration", fr: "Durée", es: "Duración" }, lang)}</div>
                        <div className="mt-1">{practical.duration}</div>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <Mountain className="w-4 h-4 text-[#C16542] mt-0.5" strokeWidth={1.5} />
                      <div>
                        <div className="overline !text-[#5C5248]">{pick({ en: "Difficulty", fr: "Difficulté", es: "Dificultad" }, lang)}</div>
                        <div className="mt-1">{practical.difficulty}</div>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <Users className="w-4 h-4 text-[#C16542] mt-0.5" strokeWidth={1.5} />
                      <div>
                        <div className="overline !text-[#5C5248]">{pick({ en: "Group", fr: "Groupe", es: "Grupo" }, lang)}</div>
                        <div className="mt-1">{practical.group}</div>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <Sun className="w-4 h-4 text-[#C16542] mt-0.5" strokeWidth={1.5} />
                      <div>
                        <div className="overline !text-[#5C5248]">{pick({ en: "Season", fr: "Saison", es: "Temporada" }, lang)}</div>
                        <div className="mt-1">{practical.season}</div>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <Tag className="w-4 h-4 text-[#C16542] mt-0.5" strokeWidth={1.5} />
                      <div>
                        <div className="overline !text-[#5C5248]">{pick({ en: "From", fr: "À partir de", es: "Desde" }, lang)}</div>
                        <div className="mt-1 font-medium text-[#C16542]">{parsePrice(practical.price) || practical.price}</div>
                      </div>
                    </li>
                  </>
                ) : (
                  <>
                    <li className="flex items-start gap-3">
                      <Compass className="w-4 h-4 text-[#C16542] mt-0.5" strokeWidth={1.5} />
                      <div>
                        <div className="overline !text-[#5C5248]">{pick({ en: "Distance", fr: "Distance", es: "Distancia" }, lang)}</div>
                        <div className="mt-1">{practical.distance}</div>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <Clock className="w-4 h-4 text-[#C16542] mt-0.5" strokeWidth={1.5} />
                      <div>
                        <div className="overline !text-[#5C5248]">{pick({ en: "Duration", fr: "Durée", es: "Duración" }, lang)}</div>
                        <div className="mt-1">{practical.duration}</div>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <Sun className="w-4 h-4 text-[#C16542] mt-0.5" strokeWidth={1.5} />
                      <div>
                        <div className="overline !text-[#5C5248]">{pick({ en: "Best time", fr: "Meilleur moment", es: "Mejor momento" }, lang)}</div>
                        <div className="mt-1">{practical.best_time}</div>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <Users className="w-4 h-4 text-[#C16542] mt-0.5" strokeWidth={1.5} />
                      <div>
                        <div className="overline !text-[#5C5248]">{pick({ en: "Access", fr: "Accès", es: "Acceso" }, lang)}</div>
                        <div className="mt-1">{practical.access}</div>
                      </div>
                    </li>
                  </>
                )}
              </ul>

              <button
                type="button"
                onClick={scrollToForm}
                className="mt-8 w-full bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-6 py-3.5 text-xs tracking-[0.25em] uppercase inline-flex items-center justify-center gap-2"
                data-testid="activity-practical-cta"
              >
                {requestLabel}
                <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* INLINE REQUEST FORM */}
      <section
        ref={formRef}
        className="py-20 md:py-28 bg-[#1A1513] text-[#FDFBF7]"
        data-testid="activity-form-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="overline !text-[#D4A373]">{requestLabel}</p>
            <h2 className="font-serif-x mt-4 text-4xl md:text-5xl leading-[1.05]">
              {pick({
                en: "Request information or add this to your stay",
                fr: "Demander des informations ou l'ajouter à votre séjour",
                es: "Solicitar información o añadirlo a su estancia",
              }, lang)}
            </h2>
            <p className="mt-6 text-lg text-[#FDFBF7]/75 leading-relaxed">
              {pick({
                en: "Tell us when you'd like to do this — our concierge will design the day around you and confirm availability within 24 hours.",
                fr: "Dites-nous quand vous souhaitez le faire — notre conciergerie composera la journée autour de vous et confirmera la disponibilité sous 24 heures.",
                es: "Díganos cuándo desea hacerlo — nuestro conserje compondrá el día a su medida y confirmará la disponibilidad en 24 horas.",
              }, lang)}
            </p>
            <div className="mt-10 pt-6 border-t border-[#FDFBF7]/15 space-y-2 text-sm text-[#FDFBF7]/70">
              <p className="overline !text-[#FDFBF7]/50">{pick({ en: "Selection", fr: "Sélection", es: "Selección" }, lang)}</p>
              <p className="font-serif-x text-[#D4A373] text-lg">{itemTitle}</p>
              <p>{hotel.name} · {hotel.location}</p>
            </div>
          </div>

          <div className="md:col-span-7">
            {status.success ? (
              <div
                className="bg-[#FDFBF7] text-[#2C2621] p-8 md:p-10 border border-[#D4A373]"
                data-testid="activity-form-success"
              >
                <p className="overline !text-[#C16542] flex items-center gap-2">
                  <Check className="w-3.5 h-3.5" strokeWidth={2.2} />
                  {pick({ en: "Thank you", fr: "Merci", es: "Gracias" }, lang)}
                </p>
                <p className="font-serif-x mt-4 text-2xl md:text-3xl leading-snug">
                  {pick({
                    en: "Request received — our concierge will reply within 24h.",
                    fr: "Demande reçue — notre conciergerie vous répond sous 24h.",
                    es: "Solicitud recibida — nuestro conserje le responderá en 24h.",
                  }, lang)}
                </p>
                <button
                  type="button"
                  onClick={() => setStatus({ submitting: false, success: false, error: false })}
                  className="mt-8 inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#C16542] hover:text-[#A35133]"
                  data-testid="activity-form-reset"
                >
                  {pick({ en: "Send another request", fr: "Envoyer une autre demande", es: "Enviar otra solicitud" }, lang)}
                  <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
              </div>
            ) : (
              <form
                onSubmit={submitForm}
                className="bg-[#FDFBF7] text-[#2C2621] p-8 md:p-10 border border-[#FDFBF7]/15"
                data-testid="activity-form"
                noValidate
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <label className="block">
                    <span className="overline">{pick({ en: "Full name", fr: "Nom complet", es: "Nombre completo" }, lang)} *</span>
                    <input
                      type="text"
                      name="full_name"
                      required
                      value={form.full_name}
                      onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                      className="mt-2 w-full bg-transparent border-b border-[#2C2621]/25 focus:border-[#C16542] py-2.5 outline-none text-base"
                      data-testid="activity-form-name"
                    />
                  </label>
                  <label className="block">
                    <span className="overline">{pick({ en: "Email", fr: "Email", es: "Correo electrónico" }, lang)} *</span>
                    <input
                      type="email"
                      name="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="mt-2 w-full bg-transparent border-b border-[#2C2621]/25 focus:border-[#C16542] py-2.5 outline-none text-base"
                      data-testid="activity-form-email"
                    />
                  </label>
                  <label className="block">
                    <span className="overline">{pick({ en: "Phone (optional)", fr: "Téléphone (optionnel)", es: "Teléfono (opcional)" }, lang)}</span>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="mt-2 w-full bg-transparent border-b border-[#2C2621]/25 focus:border-[#C16542] py-2.5 outline-none text-base"
                      data-testid="activity-form-phone"
                    />
                  </label>
                  <label className="block">
                    <span className="overline">{pick({ en: "Guests", fr: "Voyageurs", es: "Huéspedes" }, lang)}</span>
                    <input
                      type="number"
                      name="guests"
                      min="1"
                      max="20"
                      value={form.guests}
                      onChange={(e) => setForm({ ...form, guests: e.target.value })}
                      className="mt-2 w-full bg-transparent border-b border-[#2C2621]/25 focus:border-[#C16542] py-2.5 outline-none text-base"
                      data-testid="activity-form-guests"
                    />
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="overline">{pick({ en: "Preferred date", fr: "Date souhaitée", es: "Fecha preferida" }, lang)}</span>
                    <input
                      type="date"
                      name="preferred_date"
                      value={form.preferred_date}
                      onChange={(e) => setForm({ ...form, preferred_date: e.target.value })}
                      className="mt-2 w-full bg-transparent border-b border-[#2C2621]/25 focus:border-[#C16542] py-2.5 outline-none text-base"
                      data-testid="activity-form-date"
                    />
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="overline">{pick({ en: "Anything we should know?", fr: "Quelque chose à savoir ?", es: "¿Algo que debamos saber?" }, lang)}</span>
                    <textarea
                      name="notes"
                      rows={3}
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      className="mt-2 w-full bg-transparent border border-[#2C2621]/20 focus:border-[#C16542] p-3 outline-none text-base resize-none"
                      data-testid="activity-form-notes"
                    />
                  </label>
                </div>

                <div className="mt-6 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4">
                  <p className="text-xs text-[#5C5248]">
                    {pick({
                      en: "We typically reply within 24 hours.",
                      fr: "Nous répondons généralement sous 24 heures.",
                      es: "Solemos responder en 24 horas.",
                    }, lang)}
                  </p>
                  <button
                    type="submit"
                    disabled={status.submitting}
                    className="bg-[#C16542] hover:bg-[#A35133] disabled:opacity-60 text-[#FDFBF7] px-8 py-3.5 text-xs tracking-[0.25em] uppercase inline-flex items-center justify-center gap-2"
                    data-testid="activity-form-submit"
                  >
                    {status.submitting ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} />
                        {pick({ en: "Sending…", fr: "Envoi…", es: "Enviando…" }, lang)}
                      </>
                    ) : (
                      <>
                        {pick({ en: "Send request", fr: "Envoyer la demande", es: "Enviar solicitud" }, lang)}
                        <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </>
                    )}
                  </button>
                </div>

                {status.error && (
                  <p
                    className="mt-5 text-sm text-[#A35133] border-t border-[#A35133]/30 pt-4"
                    data-testid="activity-form-error"
                  >
                    {pick({
                      en: "Something went wrong. Please try again or write to xaluca@xaluca.com.",
                      fr: "Une erreur est survenue. Réessayez ou écrivez à xaluca@xaluca.com.",
                      es: "Algo salió mal. Inténtelo de nuevo o escriba a xaluca@xaluca.com.",
                    }, lang)}
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      </section>

      {/* OTHER ACTIVITIES FROM THIS HOTEL */}
      {list.length > 1 && (
        <section className="py-20 md:py-28" data-testid="activity-related">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid md:grid-cols-12 gap-8 mb-12">
              <div className="md:col-span-6">
                <p className="overline">
                  {isExcursion
                    ? pick({ en: "More experiences", fr: "Autres expériences", es: "Más experiencias" }, lang)
                    : pick({ en: "More nearby", fr: "Autres lieux à proximité", es: "Más cerca de aquí" }, lang)}
                </p>
                <h2 className="font-serif-x mt-4 text-4xl md:text-5xl tracking-tight leading-[1.05]">
                  {pick({
                    en: `From ${hotel.name}`,
                    fr: `Depuis ${hotel.name}`,
                    es: `Desde ${hotel.name}`,
                  }, lang)}
                </h2>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {list
                .map((it, i) => ({ it, i }))
                .filter(({ i }) => i !== index)
                .slice(0, 3)
                .map(({ it, i }) => {
                  const poster = (hotel.gallery && hotel.gallery[(i + 2) % (hotel.gallery.length || 1)]) || hotel.image;
                  return (
                    <Link
                      key={`other-${type}-${i}`}
                      to={`/hotels/${hotel.slug}/${type}/${i}`}
                      className="group block relative aspect-[4/5] overflow-hidden"
                      data-testid={`activity-related-${type}-${i}`}
                      data-cms-scope={`activity.${hotel.slug}.${type}.${i}`}
                    >
                      <img src={poster} alt={pick(it.title, lang)} data-cms-role="card" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" decoding="async"/>
                      <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/90 via-[#1A1513]/20 to-transparent" />
                      <div className="absolute inset-0 p-5 flex flex-col justify-end text-[#FDFBF7]">
                        <div className="overline !text-[#D4A373]">{isExcursion ? pick({ en: "Excursion", fr: "Excursion", es: "Excursión" }, lang) : pick({ en: "Nearby", fr: "À proximité", es: "Cerca" }, lang)}</div>
                        <div className="font-serif-x text-xl md:text-2xl mt-1.5 leading-tight">{pick(it.title, lang)}</div>
                      </div>
                    </Link>
                  );
                })}
            </div>
          </div>
        </section>
      )}

      {/* BACK BAND */}
      <section className="bg-[#1A1513] text-[#FDFBF7] py-16" data-testid="activity-back-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <p className="overline !text-[#D4A373]">
              {pick({ en: "Back to the property", fr: "Retour à l'adresse", es: "Volver a la propiedad" }, lang)}
            </p>
            <h3 className="font-serif-x mt-3 text-3xl md:text-4xl leading-tight">{hotel.name}</h3>
          </div>
          <Link
            to={`/hotels/${hotel.slug}`}
            className="bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-8 py-4 text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2"
            data-testid="activity-back-to-hotel"
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
            {pick({ en: "Back to the hotel page", fr: "Retour à la page de l'hôtel", es: "Volver a la página del hotel" }, lang)}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ActivityDetailPage;
